import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GlassCard,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import type { UsagePeriodDays } from '@/versions/v1/shared/settings-seed';
import { formatDateLabel, formatTokens, formatUsd } from './format-utils';
import { capDailyRowsForDisplay, deriveUsageForPeriod } from './UsageScene.transform';

const PERIOD_OPTIONS: UsagePeriodDays[] = [7, 30, 90];

function parseDays(raw: string | null): UsagePeriodDays {
  const n = Number(raw);
  if (n === 7 || n === 30 || n === 90) return n;
  return 30;
}

export function UsageScene() {
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseDays(searchParams.get('days'));
  const summary = useMemo(() => deriveUsageForPeriod(days), [days]);
  const dailyDisplay = useMemo(
    () => capDailyRowsForDisplay(summary.daily, days),
    [summary.daily, days],
  );

  const totalTokens = summary.inputTokens + summary.outputTokens;
  const isEmpty = summary.chatTurns === 0 && summary.agentRuns === 0;
  const maxDailyCost = useMemo(
    () => dailyDisplay.rows.reduce((max, row) => Math.max(max, row.costUsd), 0),
    [dailyDisplay.rows],
  );

  const setDays = (option: UsagePeriodDays) => {
    setSearchParams({ days: String(option) });
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="UsageScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <SettingsPageHeader
              title="Usage"
              description="What this account has spent on the assistant."
            />

            <Commentable name="PeriodSelector">
              <div
                className="inline-flex rounded-lg border border-[var(--glass-border)] p-1"
                role="tablist"
                aria-label="Usage period"
              >
                {PERIOD_OPTIONS.map((option) => {
                  const active = days === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setDays(option)}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-sm transition-colors',
                        active
                          ? 'bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)] font-medium text-zinc-900 dark:text-zinc-100'
                          : 'text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100',
                      )}
                    >
                      {option} days
                    </button>
                  );
                })}
              </div>
            </Commentable>

            <Commentable name="SummaryStats">
              <GlassCard as="section" tone="strong" className="overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-[var(--glass-border)] sm:grid-cols-4">
                  <StatBlock label="Total cost" value={formatUsd(summary.totalCostUsd)} />
                  <StatBlock
                    label="Chat"
                    value={formatUsd(summary.chatCostUsd)}
                    sub={`${formatTokens(summary.chatTurns)} turns`}
                  />
                  <StatBlock
                    label="Agent"
                    value={formatUsd(summary.agentCostUsd)}
                    sub={`${formatTokens(summary.agentRuns)} runs`}
                  />
                  <StatBlock label="Total tokens" value={formatTokens(totalTokens)} />
                </div>
              </GlassCard>
            </Commentable>

            {isEmpty ? (
              <GlassCard as="section" tone="strong" className="overflow-hidden">
                <div className="px-5 py-8 text-center text-sm text-muted">
                  No usage recorded in the last {days} days.
                </div>
              </GlassCard>
            ) : (
              <>
                <Commentable name="ModelTable">
                  <GlassCard as="section" tone="strong" className="overflow-hidden">
                    <div className="border-b border-[var(--glass-border)] px-5 py-4">
                      <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                        By model
                      </h2>
                      <p className="mt-1 text-sm text-muted">
                        Chat spend broken down by model and kit.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[36rem] text-sm">
                        <thead>
                          <tr className="border-b border-[var(--glass-border)] text-left text-xs font-medium uppercase tracking-wide text-muted">
                            <th className="px-5 py-3 font-medium">Model</th>
                            <th className="px-5 py-3 font-medium">Kit</th>
                            <th className="px-5 py-3 text-right font-medium">Turns</th>
                            <th className="px-5 py-3 text-right font-medium">Input</th>
                            <th className="px-5 py-3 text-right font-medium">Output</th>
                            <th className="px-5 py-3 text-right font-medium">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.byModel.map((row) => (
                            <tr
                              key={`${row.model}-${row.kit}`}
                              className="border-b border-[var(--glass-border)] last:border-b-0"
                            >
                              <td className="px-5 py-3 text-zinc-900 dark:text-zinc-100">
                                {row.model}
                              </td>
                              <td className="px-5 py-3 text-muted">{row.kit}</td>
                              <td className="px-5 py-3 text-right tabular-nums">
                                {formatTokens(row.turns)}
                              </td>
                              <td className="px-5 py-3 text-right tabular-nums">
                                {formatTokens(row.inputTokens)}
                              </td>
                              <td className="px-5 py-3 text-right tabular-nums">
                                {formatTokens(row.outputTokens)}
                              </td>
                              <td className="px-5 py-3 text-right tabular-nums">
                                {formatUsd(row.costUsd)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>
                </Commentable>

                <Commentable name="DailySpend">
                  <GlassCard as="section" tone="strong" className="overflow-hidden">
                    <div className="border-b border-[var(--glass-border)] px-5 py-4">
                      <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                        Daily spend
                      </h2>
                      <p className="mt-1 text-sm text-muted">
                        Chat cost per day over the selected period.
                        {dailyDisplay.capped
                          ? ` Showing last ${dailyDisplay.rows.length} of ${dailyDisplay.totalDays} days.`
                          : null}
                      </p>
                    </div>
                    <ul className="space-y-3 px-5 py-4">
                      {dailyDisplay.rows.map((row) => {
                        const barWidth =
                          maxDailyCost > 0 ? (row.costUsd / maxDailyCost) * 100 : 0;
                        return (
                          <li key={row.date} className="space-y-1">
                            <div className="flex items-center justify-between gap-3 text-xs text-muted">
                              <span>{formatDateLabel(row.date)}</span>
                              <span className="tabular-nums">
                                {formatUsd(row.costUsd)} · {formatTokens(row.turns)} turns
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                              <div
                                className="h-full rounded-full bg-[color-mix(in_srgb,var(--color-accent-600)_35%,transparent)]"
                                style={{ width: `${barWidth}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </GlassCard>
                </Commentable>

                {summary.connectors.totalInvocations > 0 ? (
                  <Commentable name="ConnectorInvocations">
                    <GlassCard as="section" tone="strong" className="overflow-hidden">
                      <div className="border-b border-[var(--glass-border)] px-5 py-4">
                        <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                          Connectors
                        </h2>
                        <p className="mt-1 text-sm text-muted">
                          Tool invocations by connector (all-time).
                        </p>
                      </div>
                      <ul className="divide-y divide-[var(--glass-border)]">
                        {summary.connectors.byConnector.map((row) => (
                          <li
                            key={row.connectorId}
                            className="flex items-center justify-between px-5 py-3 text-sm"
                          >
                            <span className="text-zinc-900 dark:text-zinc-100">
                              {row.name}
                            </span>
                            <span className="tabular-nums text-muted">
                              {formatTokens(row.invocations)} invocations
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="border-t border-[var(--glass-border)] px-5 py-3 text-xs text-muted">
                        Cumulative tool-invocation counts (all-time, not dollar cost).
                      </p>
                    </GlassCard>
                  </Commentable>
                ) : null}

                {summary.embeddings.calls > 0 ? (
                  <Commentable name="Embeddings">
                    <GlassCard as="section" tone="strong" className="overflow-hidden">
                      <div className="border-b border-[var(--glass-border)] px-5 py-4">
                        <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                          Embeddings
                        </h2>
                        <p className="mt-1 text-sm text-muted">
                          Voyage embedding spend (all-time).
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-px bg-[var(--glass-border)]">
                        <StatBlock
                          label="Cost"
                          value={formatUsd(summary.embeddings.costUsd)}
                        />
                        <StatBlock
                          label="Calls"
                          value={formatTokens(summary.embeddings.calls)}
                        />
                        <StatBlock
                          label="Tokens"
                          value={formatTokens(summary.embeddings.inputTokens)}
                        />
                      </div>
                      <p className="border-t border-[var(--glass-border)] px-5 py-3 text-xs text-muted">
                        Cumulative Voyage embedding cost (all-time).
                      </p>
                    </GlassCard>
                  </Commentable>
                ) : null}
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function StatBlock({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-[var(--glass-surface-strong)] px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-muted">{sub}</p> : null}
    </div>
  );
}
